import { loadGoogleMapsScript } from '@/shared/lib/maps/googleMaps';

export type PlacePrediction = {
  placeId: string;
  primaryText: string;
  secondaryText: string;
  description: string;
};

export type ResolvedPlace = {
  placeId: string;
  label: string;
  lat: number;
  lng: number;
};

type PlacesRuntime = {
  maps: typeof google.maps;
  autocomplete: google.maps.places.AutocompleteService;
  places: google.maps.places.PlacesService;
  sessionToken: google.maps.places.AutocompleteSessionToken;
};

const cityBoundsCache = new Map<string, google.maps.LatLngBounds>();

let runtimePromise: Promise<PlacesRuntime> | null = null;

async function getPlacesRuntime(apiKey: string): Promise<PlacesRuntime> {
  if (!runtimePromise) {
    runtimePromise = (async () => {
      const maps = await loadGoogleMapsScript(apiKey);
      if (!maps.places) {
        throw new Error('Places library unavailable');
      }
      const attribution = document.createElement('div');
      attribution.setAttribute('aria-hidden', 'true');
      attribution.style.display = 'none';
      document.body.appendChild(attribution);

      return {
        maps,
        autocomplete: new maps.places.AutocompleteService(),
        places: new maps.places.PlacesService(attribution),
        sessionToken: new maps.places.AutocompleteSessionToken(),
      };
    })().catch((err) => {
      runtimePromise = null;
      throw err;
    });
  }
  return runtimePromise;
}

function refreshSessionToken(rt: PlacesRuntime) {
  rt.sessionToken = new rt.maps.places.AutocompleteSessionToken();
}

/**
 * Resolve campaign city to a viewport once (cached) so predictions bias locally
 * without repeating Geocoding charges.
 */
export async function resolveCityBounds(
  apiKey: string,
  city: string,
): Promise<google.maps.LatLngBounds | null> {
  const key = city.trim().toLowerCase();
  if (!key) return null;
  const cached = cityBoundsCache.get(key);
  if (cached) return cached;

  const rt = await getPlacesRuntime(apiKey);
  const geocoder = new rt.maps.Geocoder();

  return new Promise((resolve) => {
    geocoder.geocode({ address: city }, (results, status) => {
      if (status !== 'OK' || !results?.[0]?.geometry) {
        resolve(null);
        return;
      }
      const bounds =
        results[0].geometry.viewport ||
        (results[0].geometry.location
          ? new rt.maps.LatLngBounds(
              results[0].geometry.location,
              results[0].geometry.location,
            )
          : null);
      if (bounds) cityBoundsCache.set(key, bounds);
      resolve(bounds);
    });
  });
}

/**
 * Autocomplete predictions — billed under the active session token until
 * `resolvePlaceDetails` is called (then a new session starts).
 */
export async function fetchPlacePredictions(opts: {
  apiKey: string;
  input: string;
  country: string;
  city?: string;
  signal?: AbortSignal;
}): Promise<PlacePrediction[]> {
  const input = opts.input.trim();
  if (input.length < 2) return [];

  const rt = await getPlacesRuntime(opts.apiKey);
  if (opts.signal?.aborted) return [];

  const bounds = opts.city
    ? await resolveCityBounds(opts.apiKey, opts.city)
    : null;
  if (opts.signal?.aborted) return [];

  const request: google.maps.places.AutocompletionRequest = {
    input,
    types: ['geocode'],
    componentRestrictions: { country: opts.country },
    sessionToken: rt.sessionToken,
  };

  if (bounds) {
    request.locationBias = bounds;
  }

  return new Promise((resolve, reject) => {
    if (opts.signal?.aborted) {
      resolve([]);
      return;
    }

    rt.autocomplete.getPlacePredictions(request, (predictions, status) => {
      if (opts.signal?.aborted) {
        resolve([]);
        return;
      }
      if (
        status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS ||
        !predictions?.length
      ) {
        resolve([]);
        return;
      }
      if (status !== google.maps.places.PlacesServiceStatus.OK) {
        reject(new Error(`Places autocomplete failed: ${status}`));
        return;
      }

      resolve(
        predictions.map((p) => ({
          placeId: p.place_id || '',
          primaryText: p.structured_formatting?.main_text || p.description,
          secondaryText: p.structured_formatting?.secondary_text || '',
          description: p.description,
        })).filter((p) => p.placeId),
      );
    });
  });
}

/**
 * Place Details for a prediction. Completes the autocomplete billing session.
 */
export async function resolvePlaceDetails(opts: {
  apiKey: string;
  placeId: string;
}): Promise<ResolvedPlace | null> {
  const rt = await getPlacesRuntime(opts.apiKey);

  return new Promise((resolve, reject) => {
    rt.places.getDetails(
      {
        placeId: opts.placeId,
        fields: ['place_id', 'geometry', 'formatted_address', 'name'],
        sessionToken: rt.sessionToken,
      },
      (place, status) => {
        // New session for the next search — required for correct Places billing.
        refreshSessionToken(rt);

        if (
          status !== google.maps.places.PlacesServiceStatus.OK ||
          !place?.geometry?.location
        ) {
          if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
            resolve(null);
            return;
          }
          reject(new Error(`Place details failed: ${status}`));
          return;
        }

        const loc = place.geometry.location;
        resolve({
          placeId: place.place_id || opts.placeId,
          label:
            place.formatted_address?.trim() ||
            place.name?.trim() ||
            'Selected place',
          lat: loc.lat(),
          lng: loc.lng(),
        });
      },
    );
  });
}

/** Warm Maps + Places at modal open (no prediction charges). */
export function prefetchGooglePlaces(apiKey: string) {
  if (!apiKey) return;
  void getPlacesRuntime(apiKey).catch(() => {
    /* ignore warm-up errors */
  });
}
